import PropTypes from 'prop-types';
import React, { useContext, useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux'; 
import {
  LoadingContext,
  M_StateActions,
  MTable,
  MSelect,
  BufferService
} from 'src/M_Components';

export default function TenderPhaseConfig({ roles, onCreateTab, toastMessages }) {
  const ctx = useContext(LoadingContext);
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const M_State = useSelector((state) => state.StateKeyValue);

  // Use state for selectedTab id
  const [id, setId] = useState(() => M_State.find(obj => obj.key === 'selectedTab')?.value);

  const [tenders, setTenders] = useState([]);
  const [selectedTenderId, setSelectedTenderId] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const removeContextItems = ['Print', 'Copy', 'Create'];

  const baseUrl = ctx.DEFAULTURL;
  const tenderService = React.useMemo(() => new BufferService('Tenders', baseUrl), [baseUrl]);

  // Small helpers to be resilient to API casing changes
  const normList = (data) => {
    if (!data) return [];
    // support Items, items or raw array
    const arr = Array.isArray(data) ? data : (data.Items || data.items || []);
    return Array.isArray(arr) ? arr : [];
  };
  const getId = (row) => row?.Id ?? row?.id;
  const getName = (row) => row?.Name ?? row?.name ?? '';

  //============================================================================================================
  // Upsert session data for the current tab
  //============================================================================================================
  const upsertSession = (tabId, kv) => {
    const current = M_State.find(o => o.key === tabId)?.value || [];
    const filtered = current.filter(e => e.key !== kv.key);
    dispatch(M_StateActions.addM_State({ key: tabId, value: [...filtered, kv] }));
  };

  //============================================================================================================
  // EFFECT
  // Load initial data
  //============================================================================================================
  useEffect(() => {
    ctx.setIsLoad(true);

    // Ask the backend to filter TenderTypeId eq 2 and cap page size
    const qs = '?page=1&pagesize=1000&filter=TenderTypeId%20eq%202';

    tenderService
      .filter(qs)
      .then((data) => {
        // If API returns an error, throw it to be caught below
        if (data?.__ok === false || data?.__isApiError) throw data;

        const list = normList(data);
        // Map to {label,value} using tender name and id
        const options = list.map(t => ({
          label: getName(t),
          value: getId(t)
        })).filter(o => o.value != null);

        setTenders(options);

        // Restore selectedTenderId from session for this TAB
        const session = M_State.find(obj => obj.key === id)?.value;
        const saved = session?.find(x => x.key === 'selectedTenderId')?.value;
        if (saved != null && options.some(o => o.value === saved)) {
          setSelectedTenderId(saved);
        }
      })
      .catch(() => toastMessages('error', '', t('ErrorLoadingTenders')))
      .finally(() => ctx.setIsLoad(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  //============================================================================================================
  // EFFECT
  // Restore session data when selectedTab changes
  //============================================================================================================
  useEffect(() => {
    const newId = M_State.find(obj => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      const session = M_State.find(obj => obj.key === newId)?.value;
      const saved = session?.find(x => x.key === 'selectedTenderId')?.value;
      const savedRefresh = session?.find(x => x.key === 'refresh')?.value;
      if (saved != null) setSelectedTenderId(saved);
      if (savedRefresh !== undefined) setRefresh(savedRefresh);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  //============================================================================================================
  // EFFECT
  // Force refresh when selectedTenderId changes
  //============================================================================================================
  useEffect(() => {
    if (!id) return;
    upsertSession(id, { key: 'selectedTenderId', value: selectedTenderId ?? null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenderId]);

  //============================================================================================================
  // EFFECT
  // Upsert session data when id or refresh state changes
  //============================================================================================================
  useEffect(() => {
    if (!id) return;
    upsertSession(id, { key: 'refresh', value: refresh });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, refresh]);

  //============================================================================================================
  // Function to create new route for CRUD operations
  //============================================================================================================
  const NewRoute = (url, label, operation, data) => {
    dispatch(M_StateActions.addM_State({ key: 'operation', value: operation }));

    // Phase mode is always explicit. No phase create here, only view or edit.
    if (['Edit', 'View'].includes(operation)) {
      if (!data) {
        toastMessages('error', '', t('SelectedLine'));
        return;
      }
      dispatch(M_StateActions.addM_State({ key: 'TenderPhaseLine', value: data }));
      dispatch(M_StateActions.addM_State({ key: 'IsPhaseMode', value: true }));
    }

    // Open the unified CRUD route which will render in Phase mode based on the flags above
    const route = 'tenders/TenderConfigCrud';
    const tabLabel = `${label} "${data?.Name ?? data?.name ?? ''}"`;

    onCreateTab(route, tabLabel);
  };

  //============================================================================================================
  // Memoized replace functions for table actions
  //============================================================================================================
  const replaceFunctions = useMemo(() => new Map([
    ['View', (e) => NewRoute('/tenderPhaseConfigCrud', t('LabelViewTenderPhase'), 'View', e)],
    ['Edit', (e) => NewRoute('/tenderPhaseConfigCrud', t('LabelEditTenderPhase'), 'Edit', e)],
  ]), [t]);

  return (
    <div className="divGlobalSettings">
      <div className="subTitleSettings">
        <div>
          <div style={{ color: ctx.secondaryColor }}>{t("TenderPhases")}</div>
          <div className="labelSubtitleSettings">{t("InfoTenderPhases")}</div>
        </div>
      </div>

      <div style={{ marginBottom: '20px', width: '350px' }}>
        <MSelect
          label={t("SelectTender")}
          value={tenders.find(t => t.value === selectedTenderId) || null}
          options={tenders}
          onChangeValue={(option) => setSelectedTenderId(option?.value)}
          placeholder={t("SelectTenderPlaceholder")}
          required
          autocomplete
        />
      </div>

      {selectedTenderId && (
        <MTable
          key={`TenderPhases-${selectedTenderId}`}
          filtersOnTable
          entityName={'TenderPhases'}
          roleName={'Tenders'}
          rolePermissions={roles?.map(vl => vl.Value)}
          removeContextItems={removeContextItems}
          //refresh={refresh}
          //setRefresh={setRefresh}
          useSessionStorage={false}
          FILTER={`TenderId eq ${selectedTenderId}`}
          replaceFunctions={replaceFunctions}
          tabsRailWidth={250}
        />
      )}
    </div>
  );
}

TenderPhaseConfig.propTypes = {
  roles: PropTypes.array,
  onCreateTab: PropTypes.func,
  toastMessages: PropTypes.func
};
